import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ExtensionIcon from "@mui/icons-material/Extension";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { toPng } from "html-to-image";
import { markScreenshotNotReady, signalScreenshotSceneReady } from "../../../lib/screenshotReady";
import ScreenshotExpiredPage from "../../mobile-sliding/components/ScreenshotExpiredPage";
import LetterWheel, { buildWheelTiles, lettersForWheel } from "../components/LetterWheel";
import PlayBackground from "../components/PlayBackground";
import ScreenshotPlayPanel from "../components/ScreenshotPlayPanel";
import { resolveStandaloneBackgroundUrl } from "../lib/screenshotStoryBackground";
import { normalizeWord } from "../lib/gridReveal";
import { isScreenshotSceneExpired } from "../lib/screenshotAccess";
import { loadScreenshotWordWheel } from "../lib/screenshotApi";
import { resolveScreenshotPlayColors } from "../lib/theme";
import { FEATURE_GRAPHIC_PATH, WORD_WHEEL_QUEST_TITLE } from "../routes";
import { getScreenshotScene, isScreenshotSceneConfigured } from "../screenshotScenes";
import { WORD_WHEEL_APP_ICON_ART } from "./AppIconPage";

/** Google Play Feature Graphic canvas size. */
export const FEATURE_GRAPHIC_W = 1024;
export const FEATURE_GRAPHIC_H = 500;

const SAVE_BAR_H = 64;
const FEATURE_GRAPHIC_FILE_NAME = "word-wheel-quest-feature-graphic-1024x500.png";

/** Back phone is level 10. Front phone is level 100 on the shot 01 photo. */
const PLAY_SCENE_SLUGS = ["02", "03"];
const HERO_SCENE_SLUG = "01";
const WHEEL_SCENE_SLUG = "05";

const TITLE_STACK =
    '"Segoe UI", "Arial Black", "Helvetica Neue", Impact, sans-serif';
const TAGLINE_STACK =
    'Georgia, "Palatino Linotype", "Book Antiqua", Palatino, "Times New Roman", serif';

const FEATURE_ITEMS = [
    { label: "Daily Puzzles", Icon: TimerOutlinedIcon },
    { label: "1000+ Levels", Icon: ExtensionIcon },
    { label: "Smart Hints", Icon: LightbulbOutlinedIcon },
];

const FEATURE_CHIP_HIGHLIGHT_MS = 2000;
const FEATURE_CHIP_INTRO_PAUSE_MS = 3000;

/** Soft drifting haze for 1080p ending capture (subtle, behind phones/copy). */
const ENDING_MIST_BLOBS = [
    {
        top: "-18%",
        left: "-12%",
        w: "55%",
        h: "70%",
        background:
            "radial-gradient(ellipse at 40% 50%, rgba(186,230,253,0.22) 0%, rgba(56,189,248,0.08) 45%, transparent 72%)",
        duration: "18s",
        delay: "0s",
    },
    {
        top: "25%",
        left: "38%",
        w: "50%",
        h: "65%",
        background:
            "radial-gradient(ellipse at 50% 40%, rgba(125,211,252,0.16) 0%, rgba(14,165,233,0.06) 48%, transparent 70%)",
        duration: "22s",
        delay: "-6s",
    },
    {
        top: "-8%",
        right: "-10%",
        w: "48%",
        h: "60%",
        background:
            "radial-gradient(ellipse at 60% 45%, rgba(224,242,254,0.18) 0%, rgba(56,189,248,0.07) 42%, transparent 68%)",
        duration: "20s",
        delay: "-10s",
    },
];

const ENDING_MIST_BUBBLES = [
    { left: "8%", bottom: "4%", size: 18, delay: "0s", duration: "9s", sway: "14px" },
    { left: "18%", bottom: "0%", size: 12, delay: "1.4s", duration: "7s", sway: "-10px" },
    { left: "72%", bottom: "6%", size: 22, delay: "0.6s", duration: "10s", sway: "-16px" },
    { left: "86%", bottom: "2%", size: 14, delay: "2.2s", duration: "8s", sway: "12px" },
    { left: "42%", bottom: "1%", size: 10, delay: "1.1s", duration: "8.5s", sway: "8px" },
    { left: "58%", bottom: "5%", size: 16, delay: "0.4s", duration: "10.5s", sway: "-9px" },
];

function EndingCaptureMist() {
    return (
        <Box
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                overflow: "hidden",
                pointerEvents: "none",
                zIndex: 1,
                "@keyframes wwEndingMistDrift": {
                    "0%": { transform: "translate3d(0, 0, 0) scale(1)" },
                    "50%": { transform: "translate3d(4%, -3%, 0) scale(1.06)" },
                    "100%": { transform: "translate3d(-3%, 2%, 0) scale(1.02)" },
                },
                "@keyframes wwEndingBubbleRise": {
                    "0%": {
                        transform: "translate3d(0, 0, 0) scale(0.8)",
                        opacity: 0,
                    },
                    "12%": { opacity: 0.45 },
                    "55%": {
                        transform: "translate3d(var(--sway), -45%, 0) scale(1)",
                        opacity: 0.28,
                    },
                    "100%": {
                        transform: "translate3d(calc(var(--sway) * -0.4), -110%, 0) scale(0.9)",
                        opacity: 0,
                    },
                },
            }}
        >
            {ENDING_MIST_BLOBS.map((blob, i) => (
                <Box
                    key={`mist-${i}`}
                    sx={{
                        position: "absolute",
                        top: blob.top,
                        left: blob.left,
                        right: blob.right,
                        width: blob.w,
                        height: blob.h,
                        background: blob.background,
                        filter: "blur(28px)",
                        opacity: 0.85,
                        willChange: "transform",
                        animation: `wwEndingMistDrift ${blob.duration} ease-in-out ${blob.delay} infinite alternate`,
                    }}
                />
            ))}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(180deg, rgba(186,230,253,0.06) 0%, transparent 35%, transparent 65%, rgba(125,211,252,0.08) 100%)",
                }}
            />
            {ENDING_MIST_BUBBLES.map((b, i) => (
                <Box
                    key={`bubble-${i}`}
                    sx={{
                        position: "absolute",
                        left: b.left,
                        bottom: b.bottom,
                        width: b.size,
                        height: b.size,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle at 32% 28%, rgba(255,255,255,0.7) 0%, rgba(186,230,253,0.35) 40%, rgba(125,211,252,0.12) 70%, transparent 100%)",
                        border: "1px solid rgba(224,242,254,0.4)",
                        boxShadow: "0 0 10px rgba(125,211,252,0.2)",
                        willChange: "transform, opacity",
                        "--sway": b.sway,
                        animation: `wwEndingBubbleRise ${b.duration} linear ${b.delay} infinite`,
                    }}
                />
            ))}
        </Box>
    );
}

const STAR_DOTS = [
    [560, 40], [610, 70], [670, 35], [720, 90], [780, 55], [840, 80],
    [900, 45], [950, 100], [580, 160], [640, 200], [700, 140], [760, 210],
    [820, 170], [890, 230], [940, 180], [600, 280], [680, 320], [750, 290],
    [830, 340], [910, 300], [970, 350], [620, 400], [710, 440], [800, 410],
    [880, 460], [960, 430],
];

const CONSTELLATION_LINES = [
    "M 610 70 L 670 35 L 720 90 L 780 55",
    "M 840 80 L 900 45 L 950 100",
    "M 640 200 L 700 140 L 760 210 L 820 170",
    "M 750 290 L 830 340 L 910 300",
];

function parseWords(wordsInUse) {
    if (!wordsInUse) return [];
    return wordsInUse.split("\n").map((w) => w.trim()).filter(Boolean);
}

function foundWordsForPuzzle(puzzle, demoFoundWordCount) {
    if (!puzzle?.wordsInUse) return [];
    const words = parseWords(puzzle.wordsInUse).map(normalizeWord);
    const count = Math.max(0, Number(demoFoundWordCount) || 0);
    return words.slice(0, Math.min(count, words.length));
}

function FeatureGraphicLeftCopy({ cycleChips = false, activeIndex = 0 }) {
    return (
        <Box
            sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 410,
                zIndex: 5,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                pl: 4.5,
                pr: 2,
                boxSizing: "border-box",
                pointerEvents: "none",
                textAlign: "left",
            }}
        >
            <Box
                component="img"
                src={WORD_WHEEL_APP_ICON_ART}
                alt=""
                sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "18px",
                    objectFit: "cover",
                    display: "block",
                    mb: 1.75,
                    boxShadow:
                        "0 8px 28px rgba(0,0,0,0.45), 0 0 0 2px rgba(56,189,248,0.45), 0 0 22px rgba(34,211,238,0.35)",
                    userSelect: "none",
                }}
            />
            <Typography
                component="h1"
                sx={{
                    m: 0,
                    fontFamily: TITLE_STACK,
                    fontWeight: 900,
                    fontSize: 42,
                    lineHeight: 0.94,
                    letterSpacing: "0.045em",
                    textTransform: "uppercase",
                    background:
                        "linear-gradient(180deg, #f8fafc 0%, #bae6fd 28%, #38bdf8 55%, #94a3b8 78%, #e2e8f0 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    filter:
                        "drop-shadow(0 0 10px rgba(14, 165, 233, 0.95)) drop-shadow(0 0 28px rgba(56, 189, 248, 0.75)) drop-shadow(0 4px 2px rgba(2, 6, 23, 0.85))",
                    maxWidth: 420,
                    userSelect: "none",
                }}
            >
                {WORD_WHEEL_QUEST_TITLE}
            </Typography>
            <Typography
                sx={{
                    mt: 1.5,
                    mb: 2.25,
                    fontFamily: TAGLINE_STACK,
                    fontStyle: "italic",
                    fontSize: 17,
                    lineHeight: 1.35,
                    color: "rgba(186, 230, 253, 0.92)",
                    maxWidth: 400,
                    textShadow: "0 2px 10px rgba(2,6,23,0.85)",
                    userSelect: "none",
                }}
            >
                Swipe the wheel. Find every word.
            </Typography>
            <Stack spacing={1} sx={{ mb: 2.5 }}>
                {FEATURE_ITEMS.map(({ label, Icon }, index) => {
                    const highlighted = cycleChips && index === activeIndex;
                    const dimmed = cycleChips && !highlighted;
                    return (
                    <Stack
                        key={label}
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                            px: 1.25,
                            py: 0.55,
                            borderRadius: 999,
                            bgcolor: highlighted
                                ? "rgba(14, 165, 233, 0.42)"
                                : "rgba(2, 6, 23, 0.55)",
                            border: highlighted
                                ? "1.5px solid #38bdf8"
                                : "1px solid rgba(56, 189, 248, 0.35)",
                            width: "fit-content",
                            boxShadow: highlighted
                                ? "0 0 22px rgba(56,189,248,0.65), 0 0 8px rgba(125,211,252,0.45)"
                                : "0 0 16px rgba(14,165,233,0.2)",
                            opacity: dimmed ? 0.38 : 1,
                            transform: highlighted ? "scale(1.08) translateX(6px)" : "none",
                            transition:
                                "opacity 0.35s ease, transform 0.35s ease, box-shadow 0.35s ease, background 0.35s ease, border-color 0.35s ease",
                        }}
                    >
                        <Icon
                            sx={{
                                fontSize: highlighted ? 20 : 18,
                                color: highlighted ? "#ffffff" : "#7dd3fc",
                                filter: highlighted
                                    ? "drop-shadow(0 0 6px rgba(186,230,253,0.9))"
                                    : "none",
                                transition: "font-size 0.35s ease, filter 0.35s ease",
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: 13,
                                fontWeight: highlighted ? 800 : 700,
                                letterSpacing: "0.04em",
                                color: highlighted ? "#ffffff" : "#e0f2fe",
                                userSelect: "none",
                                transition: "color 0.35s ease, font-weight 0.35s ease",
                            }}
                        >
                            {label}
                        </Typography>
                    </Stack>
                    );
                })}
            </Stack>
            <Box
                sx={{
                    px: 2.5,
                    py: 1,
                    borderRadius: 999,
                    background:
                        "linear-gradient(90deg, #0ea5e9 0%, #38bdf8 50%, #22d3ee 100%)",
                    boxShadow:
                        "0 0 24px rgba(34,211,238,0.55), 0 8px 20px rgba(0,0,0,0.4)",
                }}
            >
                <Typography
                    sx={{
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: 15,
                        letterSpacing: "0.12em",
                        color: "#0c4a6e",
                        userSelect: "none",
                    }}
                >
                    PLAY NOW
                </Typography>
            </Box>
        </Box>
    );
}

function PhoneShell({ width, height, rotate, top, left, zIndex, children }) {
    return (
        <Box
            sx={{
                position: "absolute",
                top,
                left,
                width,
                height,
                transform: `rotate(${rotate}deg)`,
                transformOrigin: "center center",
                zIndex,
                pointerEvents: "none",
            }}
        >
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: -16,
                    borderRadius: "36px",
                    background:
                        "radial-gradient(circle at 50% 40%, rgba(56,189,248,0.28) 0%, transparent 68%)",
                    filter: "blur(6px)",
                    zIndex: 0,
                }}
            />
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    borderRadius: "28px",
                    p: "6px",
                    background:
                        "linear-gradient(145deg, rgba(148,163,184,0.55) 0%, rgba(15,23,42,0.98) 42%, rgba(56,189,248,0.35) 100%)",
                    boxShadow:
                        "0 0 0 1px rgba(186,230,253,0.3), 0 18px 40px rgba(0,0,0,0.55), 0 0 36px rgba(56,189,248,0.18)",
                    zIndex: 1,
                    boxSizing: "border-box",
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "22px",
                        overflow: "hidden",
                        bgcolor: "#0f172a",
                        boxShadow: "inset 0 0 0 1px rgba(14,165,233,0.3)",
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                    }}
                >
                    {children}
                </Box>
            </Box>
        </Box>
    );
}

function WheelOnlyPanel({ puzzle, background = "island", theme = "random" }) {
    const playColors = useMemo(() => resolveScreenshotPlayColors(theme), [theme]);
    const targetWords = useMemo(
        () => parseWords(puzzle?.wordsInUse).map(normalizeWord),
        [puzzle],
    );
    const wheelTiles = useMemo(() => {
        const letters = lettersForWheel(targetWords, puzzle?.lettersGrid, puzzle?.details);
        return buildWheelTiles(letters, puzzle?.id || "feature-wheel");
    }, [targetWords, puzzle]);
    const selectedIndices = useMemo(() => {
        if (wheelTiles.length < 3) return [];
        return [0, 2, Math.min(4, wheelTiles.length - 1)];
    }, [wheelTiles]);
    const wheelColors = useMemo(
        () => ({
            ...playColors,
            surface: playColors.letterBg,
            border: playColors.letterBorder,
            textOnSurface: playColors.letterText,
        }),
        [playColors],
    );

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
            }}
        >
            <PlayBackground background={background} theme={theme} />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    width: "78%",
                    aspectRatio: "1 / 1",
                    maxWidth: 220,
                }}
            >
                <LetterWheel
                    tiles={wheelTiles}
                    selectedIndices={selectedIndices}
                    fill
                    colors={wheelColors}
                    backdrop="helm"
                />
            </Box>
        </Box>
    );
}

/**
 * Google Play Feature Graphic — 1024×500.
 * Left: branding. Right: three phones (2 play + 1 wheel), inspired by store art (not a copy).
 */
export default function FeatureGraphicPage({ endingCapture = false }) {
    const playScenes = useMemo(
        () => PLAY_SCENE_SLUGS.map((slug) => getScreenshotScene(slug)).filter(Boolean),
        [],
    );
    const wheelScene = useMemo(() => getScreenshotScene(WHEEL_SCENE_SLUG), []);
    const heroScene = useMemo(() => getScreenshotScene(HERO_SCENE_SLUG), []);
    const [heroBackground, setHeroBackground] = useState(heroScene?.background || "iphone_1");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [puzzlesBySlug, setPuzzlesBySlug] = useState({});
    const [previewScale, setPreviewScale] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [chipHighlightIndex, setChipHighlightIndex] = useState(null);
    const frameRef = useRef(null);

    const expired = isScreenshotSceneExpired(WHEEL_SCENE_SLUG);

    useEffect(() => {
        const slidingId = heroScene?.backgroundSlidingId || "";
        if (!slidingId) return undefined;
        let cancelled = false;
        (async () => {
            const url = await resolveStandaloneBackgroundUrl({ slidingId });
            if (!cancelled && url) setHeroBackground(url);
        })();
        return () => {
            cancelled = true;
        };
    }, [heroScene]);

    useEffect(() => {
        if (expired) {
            setLoading(false);
            return undefined;
        }
        const scenes = [...playScenes, wheelScene].filter(
            (s, i, arr) => s && isScreenshotSceneConfigured(s) && arr.findIndex((x) => x.slug === s.slug) === i,
        );
        if (scenes.length < 2) {
            setError("Feature graphic scenes are not configured");
            setLoading(false);
            return undefined;
        }

        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const entries = await Promise.all(
                    scenes.map(async (scene) => {
                        const data = await loadScreenshotWordWheel(scene);
                        return [scene.slug, data];
                    }),
                );
                if (cancelled) return;
                setPuzzlesBySlug(Object.fromEntries(entries));
            } catch (e) {
                if (!cancelled) setError(e?.message || "Failed to load puzzles");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [expired, playScenes, wheelScene]);

    const ready = playScenes.every((s) => puzzlesBySlug[s.slug])
        && Boolean(puzzlesBySlug[WHEEL_SCENE_SLUG]);
    const chipsCycling = chipHighlightIndex != null;

    useEffect(() => {
        if (!endingCapture || loading || error || !ready) {
            setChipHighlightIndex(null);
            return undefined;
        }
        setChipHighlightIndex(null);
        const startId = window.setTimeout(() => {
            setChipHighlightIndex(0);
        }, FEATURE_CHIP_INTRO_PAUSE_MS);
        return () => window.clearTimeout(startId);
    }, [endingCapture, loading, error, ready]);

    useEffect(() => {
        if (!chipsCycling) return undefined;
        const id = window.setInterval(() => {
            setChipHighlightIndex((prev) => ((prev ?? 0) + 1) % FEATURE_ITEMS.length);
        }, FEATURE_CHIP_HIGHLIGHT_MS);
        return () => window.clearInterval(id);
    }, [chipsCycling]);

    useEffect(() => {
        if (expired || loading || error || !ready) {
            markScreenshotNotReady();
            return undefined;
        }
        signalScreenshotSceneReady({
            path: FEATURE_GRAPHIC_PATH,
            slug: "feature-graphic",
        });
        return undefined;
    }, [expired, loading, error, ready]);

    useEffect(() => {
        const updateScale = () => {
            if (endingCapture) {
                const vw = window.innerWidth;
                const vh = window.innerHeight;
                // Cover the full 1920×1080 viewport (may crop edges; no letterbox border).
                setPreviewScale(
                    Math.max(vw / FEATURE_GRAPHIC_W, vh / FEATURE_GRAPHIC_H),
                );
                return;
            }
            const padding = 32;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - SAVE_BAR_H - padding;
            setPreviewScale(
                Math.min(1, availableW / FEATURE_GRAPHIC_W, availableH / FEATURE_GRAPHIC_H),
            );
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, [endingCapture]);

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, []);

    const handleSave = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;

        setSaving(true);
        setSaveError(null);
        const previousTransform = node.style.transform;
        node.style.transform = "none";

        try {
            await new Promise((resolve) =>
                requestAnimationFrame(() => requestAnimationFrame(resolve)),
            );
            const dataUrl = await toPng(node, {
                width: FEATURE_GRAPHIC_W,
                height: FEATURE_GRAPHIC_H,
                pixelRatio: 1,
                cacheBust: true,
                skipFonts: true,
                style: {
                    transform: "none",
                    transformOrigin: "top left",
                },
            });
            const link = document.createElement("a");
            link.download = FEATURE_GRAPHIC_FILE_NAME;
            link.href = dataUrl;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (e) {
            setSaveError(e?.message || "Failed to save image");
        } finally {
            node.style.transform = previousTransform;
            setSaving(false);
        }
    }, [saving]);

    if (expired) {
        return <ScreenshotExpiredPage />;
    }

    const playA = playScenes[0];
    const playB = playScenes[1] || playScenes[0];

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#020617",
                overflow: "hidden",
                zIndex: 1,
                gap: endingCapture ? 0 : 1.5,
                pb: endingCapture ? 0 : `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    ...(endingCapture
                        ? {
                              // Cover full viewport; pin left so branding isn't cropped.
                              position: "absolute",
                              left: 0,
                              top: "50%",
                              width: FEATURE_GRAPHIC_W,
                              height: FEATURE_GRAPHIC_H,
                              transform: `translateY(-50%) scale(${previewScale})`,
                              transformOrigin: "left center",
                              overflow: "hidden",
                              flexShrink: 0,
                          }
                        : {
                              width: Math.round(FEATURE_GRAPHIC_W * previewScale),
                              height: Math.round(FEATURE_GRAPHIC_H * previewScale),
                              position: "relative",
                              overflow: "hidden",
                              flexShrink: 0,
                          }),
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame="word-wheel-feature-graphic"
                    sx={{
                        width: FEATURE_GRAPHIC_W,
                        height: FEATURE_GRAPHIC_H,
                        ...(endingCapture
                            ? {}
                            : {
                                  transform: `scale(${previewScale})`,
                                  transformOrigin: "top left",
                              }),
                        position: "relative",
                        overflow: "hidden",
                        bgcolor: "#030712",
                        boxShadow: endingCapture
                            ? "none"
                            : "0 0 0 1px rgba(148, 163, 184, 0.25)",
                    }}
                >
                    <Box
                        aria-hidden
                        sx={{
                            position: "absolute",
                            inset: 0,
                            background: `
                                radial-gradient(ellipse 50% 70% at 18% 48%, rgba(14, 116, 144, 0.4) 0%, transparent 68%),
                                radial-gradient(ellipse 45% 55% at 78% 42%, rgba(30, 58, 138, 0.35) 0%, transparent 65%),
                                linear-gradient(112deg, #020617 0%, #0c1a2e 40%, #07101f 68%, #000 100%)
                            `,
                        }}
                    />

                    {endingCapture ? <EndingCaptureMist /> : null}

                    {/* Soft constellation field on the right half */}
                    <Box
                        component="svg"
                        aria-hidden
                        viewBox={`0 0 ${FEATURE_GRAPHIC_W} ${FEATURE_GRAPHIC_H}`}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                            zIndex: 1,
                            opacity: 0.7,
                        }}
                    >
                        {CONSTELLATION_LINES.map((d) => (
                            <path
                                key={d}
                                d={d}
                                fill="none"
                                stroke="rgba(186,230,253,0.22)"
                                strokeWidth="1"
                            />
                        ))}
                        {STAR_DOTS.map(([cx, cy], i) => (
                            <circle
                                key={`${cx}-${cy}-${i}`}
                                cx={cx}
                                cy={cy}
                                r={i % 4 === 0 ? 1.8 : 1.1}
                                fill="rgba(224,242,254,0.85)"
                            />
                        ))}
                    </Box>

                    <FeatureGraphicLeftCopy
                        cycleChips={chipsCycling}
                        activeIndex={chipHighlightIndex ?? 0}
                    />

                    {loading ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <CircularProgress size={36} sx={{ color: "#38bdf8" }} />
                        </Box>
                    ) : error ? (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 4,
                            }}
                        >
                            <Typography color="error" align="center">
                                {error}
                            </Typography>
                        </Box>
                    ) : ready ? (
                        <>
                            {/* Back play phone */}
                            <PhoneShell
                                width={200}
                                height={420}
                                rotate={8}
                                top={18}
                                left={418}
                                zIndex={2}
                            >
                                <ScreenshotPlayPanel
                                    puzzle={puzzlesBySlug[playA.slug]}
                                    foundWords={foundWordsForPuzzle(
                                        puzzlesBySlug[playA.slug],
                                        playA.demoFoundWordCount,
                                    )}
                                    journeyLevel={playA.journeyLevel}
                                    background="beach"
                                    theme="random"
                                    fill
                                    compact
                                    centerContent
                                />
                            </PhoneShell>

                            {/* Wheel-only phone */}
                            <PhoneShell
                                width={196}
                                height={300}
                                rotate={-8}
                                top={178}
                                left={548}
                                zIndex={3}
                            >
                                <WheelOnlyPanel
                                    puzzle={puzzlesBySlug[WHEEL_SCENE_SLUG]}
                                    background="tropical_island"
                                    theme="random"
                                />
                            </PhoneShell>

                            {/* Front play phone — shot 01 photo behind the level 100 board */}
                            <PhoneShell
                                width={220}
                                height={452}
                                rotate={-10}
                                top={10}
                                left={612}
                                zIndex={4}
                            >
                                <ScreenshotPlayPanel
                                    puzzle={puzzlesBySlug[playB.slug]}
                                    foundWords={foundWordsForPuzzle(
                                        puzzlesBySlug[playB.slug],
                                        playB.demoFoundWordCount,
                                    )}
                                    journeyLevel={playB.journeyLevel}
                                    background={heroBackground}
                                    theme="random"
                                    fill
                                    compact
                                    centerContent
                                />
                            </PhoneShell>
                        </>
                    ) : (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography color="text.secondary">Puzzles missing.</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {!endingCapture ? (
            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    bgcolor: "rgba(2, 6, 23, 0.92)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.2)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        saving ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            <SaveAltIcon />
                        )
                    }
                    onClick={handleSave}
                    disabled={!ready || saving || loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        px: 3,
                        bgcolor: "#0ea5e9",
                        "&:hover": { bgcolor: "#0284c7" },
                    }}
                >
                    {saving ? "Saving…" : "Save 1024×500 PNG"}
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : (
                    <Typography variant="caption" sx={{ color: "rgba(148,163,184,0.85)", lineHeight: 1.2 }}>
                        {WORD_WHEEL_QUEST_TITLE} · Feature graphic · {FEATURE_GRAPHIC_W}×
                        {FEATURE_GRAPHIC_H} · PNG
                    </Typography>
                )}
            </Box>
            ) : null}
        </Box>
    );
}
